import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import clsx from 'clsx'
import { useTrackChangesQuery } from 'components/WorkspaceEditor/components/TrackChangesPanel/api'
import {
    UOM_MAX_LENGTH,
    UOM_MAX_SPECIAL_CHARS,
    UOMPillInput,
    useUOMSave,
} from 'components/WorkspaceEditor/features/uomInsert'
import { useEnsureParagraphSaved } from 'components/WorkspaceEditor/hooks/useEnsureParagraphSaved'
import {
    useContentMutationService,
    useTransactionManager,
} from 'components/WorkspaceEditor/services'
import { useOptionGroupStyles } from 'components/WorkspaceEditor/styles/useOptionGroupStyles'
import {
    getDisabledStyleClasses,
    nullOrUndefinedResolver,
} from 'components/WorkspaceEditor/utils'
import {
    useProjectSettings,
    useShallowSelector,
} from 'components/WorkspaceEditor/Workspace2Store'
import { TrackChangeType } from 'models/changes'
import { UnitsOfMeasureKey } from 'models/projectSettings'
import React, { memo, useRef } from 'react'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import { ReplacementOption } from './ReplacementOption'
import { SelectionOption } from './SelectionOption'

/** Props for the UnitsOfMeasure component. */
export interface UnitsOfMeasureProps {
    /** The content item to render */
    contentItem: Partial<TextContentElement>
    /** Whether the component is inactive */
    inactive?: boolean
    /** Whether the component is disabled (not hidden, just styled as disabled) */
    disabled?: boolean
    /** Whether the component is editable */
    editable?: boolean
    /** The parent content item view node — when provided, enables inline editing */
    parentContentItem?: Partial<TextContentElement>
    /** Ref to the pill's root DOM element, used to scope the focusout listener */
    pillRef?: React.RefObject<HTMLElement>
    /** Called when the user presses Escape — removes the whole pill */
    onCancel?: () => void
}

/**
 * A component that apply styling to render UnitsOfMeasure content
 * @param {Partial<TextContentElement>} contentItem - the object that holds the text property that needs to render imperial and metric units.
 */
export const UnitsOfMeasure = memo<UnitsOfMeasureProps>(
    ({
        contentItem,
        inactive = false,
        disabled = false,
        editable = false,
        parentContentItem,
        pillRef,
        onCancel,
    }) => {
        const { id, imperialUnits, metricUnits } = nullOrUndefinedResolver(
            contentItem
        )
        const { isCurrentlyOnOMWorkspace } = useOMWorkspace()

        const {
            editableText,
            editableTextBoxed,
            disabledBoxedInputContainer,
            disabledOptionGroup,
            notDisabledBoxedInputContainer,
            uomContainerInlineFlex,
            uomContainerContent,
            uomGroupWrapper,
            uomMetricUnitWrapper,
            uomPillInput,
        } = useOptionGroupStyles()

        const isBreakWord = imperialUnits.length >= 50
        const disabledClasses = getDisabledStyleClasses(
            disabled,
            inactive,
            editable,
            {
                disabledOptionGroup,
                disabledBoxedInputContainer,
                notDisabledBoxedInputContainer,
            }
        )
        const optionClasses = clsx(
            editableText,
            editableTextBoxed,
            disabledClasses
        )
        const containerClass = isBreakWord
            ? uomContainerContent
            : uomContainerInlineFlex

        const projectSettings = useProjectSettings()
        const upperCaseUOM = (
            projectSettings?.unitOfMeasure.toString() ??
            UnitsOfMeasureKey.IMPERIAL
        ).toUpperCase()
        const isBothUnitsActive = upperCaseUOM === UnitsOfMeasureKey.BOTH
        const isImperialUnitsActive =
            upperCaseUOM === UnitsOfMeasureKey.IMPERIAL
        const isMetricUnitsActive = upperCaseUOM === UnitsOfMeasureKey.METRIC

        const { mutateContentElements } = useContentMutationService()
        const { executeWithOptimistic } = useTransactionManager()
        const { fetchTrackChangesDataWithApolloClient } = useTrackChangesQuery()
        const { setContentItemViewNodes } = useShallowSelector((s) => ({
            setContentItemViewNodes: s.actions.setContentItemViewNodes,
        }))
        const { ensureParentParagraphSaved } = useEnsureParagraphSaved()

        // When pillRef isn't injected by UOMPillContent (e.g. standalone UOM rendered
        // directly by NodeContentRenderer), fall back to the component's own span so
        // the focusout listener still fires and the save still happens.
        const fallbackPillRef = useRef<HTMLSpanElement>(null)
        const effectivePillRef: React.RefObject<HTMLElement> =
            pillRef ?? fallbackPillRef

        const {
            localImperial,
            localMetric,
            cancelUOM,
            handleNormalUOMChange,
            handleUOMLocalChange,
            imperialContainerRef,
            metricContainerRef,
        } = useUOMSave({
            contentItem,
            parentContentItem,
            pillRef: effectivePillRef,
            onCancel,
            imperialUnits,
            metricUnits,
            setContentItemViewNodes,
            mutateContentElements,
            executeWithOptimistic,
            fetchTrackChangesDataWithApolloClient,
            ensureParentParagraphSaved,
        })

        const shouldRenderAsTextInput =
            (editable && !!parentContentItem) || disabled
        const isNewUOM = contentItem.changeType === TrackChangeType.ADDED

        const renderOption = (
            unitValue: string,
            type: 'imperial' | 'metric'
        ) => {
            // Unique id per unit to avoid CKEditor toggle conflicts between imperial and metric
            const virtualId = type === 'metric' ? `${id}-metric` : id
            // Use local state for display so the value persists while focus moves within the pill
            const localValue = type === 'imperial' ? localImperial : localMetric
            const containerRef =
                type === 'imperial' ? imperialContainerRef : metricContainerRef

            return (
                <div
                    ref={containerRef}
                    key={`units-of-measure-${type}-${id || 'default'}`}
                    data-testid={`units-of-measure-${type}-${id || 'default'}`}
                    data-value={localValue}
                    className={containerClass}
                >
                    {editable &&
                    parentContentItem &&
                    (pillRef || isCurrentlyOnOMWorkspace) &&
                    !inactive &&
                    !disabled ? (
                        <UOMPillInput
                            localValue={localValue}
                            className={uomPillInput}
                            parentContentItemId={parentContentItem?.id}
                            onCancel={cancelUOM}
                            unitType={type}
                            onValueChange={handleUOMLocalChange}
                            autoFocus={
                                isNewUOM &&
                                (type === 'imperial' || isMetricUnitsActive)
                            }
                        />
                    ) : shouldRenderAsTextInput ? (
                        <ReplacementOption
                            contentItem={{
                                ...contentItem,
                                id: virtualId,
                                text: localValue,
                                elements: [],
                            }}
                            classes={optionClasses}
                            disabled={disabled}
                            editable={editable}
                            maxLength={UOM_MAX_LENGTH}
                            maxSpecialChars={UOM_MAX_SPECIAL_CHARS}
                            onValueChange={(updatedItem) =>
                                handleNormalUOMChange(updatedItem, type)
                            }
                        />
                    ) : (
                        <SelectionOption
                            contentItem={contentItem}
                            label={unitValue}
                            classes={optionClasses}
                            disabled={disabled}
                            editable={editable}
                        />
                    )}
                </div>
            )
        }

        return (
            <span
                ref={fallbackPillRef}
                className={uomGroupWrapper}
                data-suggestion={`units-of-measure-${id || 'default'}`}
                onClick={(e) => e.stopPropagation()}
                style={
                    editable
                        ? { pointerEvents: 'auto', cursor: 'text' }
                        : undefined
                }
            >
                {(isImperialUnitsActive || isBothUnitsActive) &&
                    renderOption(imperialUnits, 'imperial')}
                {isMetricUnitsActive && renderOption(metricUnits, 'metric')}
                {isBothUnitsActive && (
                    <>
                        <span className={uomMetricUnitWrapper}>(</span>
                        {renderOption(metricUnits, 'metric')}
                        <span>)</span>
                    </>
                )}
            </span>
        )
    }
)
